'use client'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { DateIcon } from '@repo/ui/DateIcon'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { FilterIcon } from '@repo/ui/FilterIcon'
import { formatDate } from '@repo/utils/time'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { parse, isValid as isValidDate } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import MUIButton from '@mui/material/Button'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const DateSelection = ({
  format = 'EEE d MMM yyyy',
  value = new Date(),
  onChange = () => null,
  setBeforeAfterOpen = () => null,
  label,
  sx = {},
  required = false,
  size = 'small',
  timeRecordingDateFilter = false,
  dataGridFilter = false,
  showAdornment = false,
  minValue = null,
  maxValue = null,
  name = 'date',
  errors,
  clearable = true,
  enableTypedDdMmYyyy = false,
  largeAdornment = false,
  disabled = false,
  readOnly = false,
  isInlineEditMode = false
}) => {
  const anchorEl = useRef(null)
  const datePickerRef = useRef(null)
  const fieldInputRef = useRef(null)
  const [committedValue, setCommittedValue] = useState(value ?? null)
  const [draftInput, setDraftInput] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [touched, setTouched] = useState(false)

  const toggleDatePicker = (e) => {
    if (e) e.stopPropagation()
    if (!touched) setTouched(true)
    setIsOpen(!isOpen)
  }

  const defer = (fn) => setTimeout(fn, 0)

  const height = size === 'small' ? '40px' : '53px'

  const muiStackSx = timeRecordingDateFilter
    ? { cursor: 'pointer', height: '40px', position: 'relative', ...sx }
    : { position: 'relative', ...sx }

  const datePickerSlots = showAdornment
    ? {
        inputAdornment: Adornment
      }
    : {
        openPickerIcon: DateIcon
      }

  const hasExternalError = Boolean(errors && name && errors[name])
  const errorMessage = hasExternalError ? errors[name] : ''

  const DISPLAY_FORMAT = 'EEE d MMM yyyy'
  const TYPED_FORMAT = 'dd MM yyyy'
  const effectiveFormat = enableTypedDdMmYyyy ? (isEditing ? TYPED_FORMAT : DISPLAY_FORMAT) : format

  const parseTypedDate = (raw) => {
    if (!raw) return null
    const normalized = raw.replace(/[^\d]/g, ' ').replace(/\s+/g, ' ').trim()
    const parts = normalized.split(' ')
    if (parts.length !== 3) return null
    const [d, m, y] = parts.map((p) => parseInt(p, 10))
    if (!d || !m || !y || String(y).length !== 4) return null
    const parsed = parse(`${d} ${m} ${y}`, 'd M yyyy', new Date())
    return isValidDate(parsed) ? parsed : null
  }

  useEffect(() => {
    setCommittedValue(value ?? null)
  }, [value])

  useEffect(() => {
    if (!isInlineEditMode) return undefined
    const timeoutId = setTimeout(() => setIsOpen(true), 0)
    return () => clearTimeout(timeoutId)
  }, [isInlineEditMode])

  return (
    <MUIStack justifyContent="center" alignItems="center" data-testid="DateSelection" sx={muiStackSx}>
      {timeRecordingDateFilter ? (
        <>
          <MUITypography
            variant="h5"
            sx={{
              cursor: 'pointer',
              fontWeight: 600,
              mb: 0,
              position: 'relative',
              width: '100%'
            }}
            onClick={toggleDatePicker}
            ref={anchorEl}
          >
            {formatDate(value, 'EEE d MMMM')}{' '}
            <ChevronRightIcon sx={{ transform: 'rotate(90deg)', position: 'absolute', top: '-2px' }} />
          </MUITypography>
          <MUIButton
            sx={{
              bottom: 0,
              cursor: 'pointer',
              height: '100%',
              left: 0,
              opacity: 0,
              position: 'absolute',
              right: 0,
              top: 0,
              width: '100%',
              zIndex: 2,
              minWidth: '64px'
            }}
            onClick={toggleDatePicker}
            ref={anchorEl}
          >
            Select date
          </MUIButton>
        </>
      ) : dataGridFilter ? (
        <>
          <MUIStack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ padding: '2px 6px', width: '100%' }}
          >
            <MUITypography
              variant="body 2"
              sx={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: 600,
                mb: 0,
                position: 'relative',
                width: '100%'
              }}
              onClick={toggleDatePicker}
              ref={anchorEl}
            >
              {value !== null ? formatDate(value, format) : 'Select date'}
              <MUITooltip title={value !== null ? formatDate(value) : 'Select date'} placement="bottom" arrow>
                <DateIcon
                  sx={{
                    ml: value !== null ? '12px' : '4px',
                    height: '14px',
                    width: '14px',
                    position: 'relative',
                    top: '1px'
                  }}
                />
              </MUITooltip>
            </MUITypography>
            <MUIButton
              onClick={() => setBeforeAfterOpen((prev) => !prev)}
              sx={{ width: '14px', minWidth: '14px', position: 'relative', ml: '4px', padding: 0 }}
            >
              <FilterIcon sx={{ height: '14px', width: '14px' }} />
            </MUIButton>
          </MUIStack>
        </>
      ) : null}

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          closeOnSelect
          disabled={disabled}
          readOnly={readOnly}
          label={
            <>
              {errorMessage || label || ''}
              {required ? <span style={{ color: '#C33241' }}> *</span> : null}
            </>
          }
          views={['year', 'month', 'day']}
          onAccept={(accepted) => {
            if (accepted === null) {
              setCommittedValue(null)
              onChange(null)
              if (enableTypedDdMmYyyy) setIsEditing(false)
              return
            }
            if (!(accepted instanceof Date) || Number.isNaN(accepted.getTime())) return
            setCommittedValue(accepted)
            onChange(accepted)
            if (enableTypedDdMmYyyy) setIsEditing(false)
          }}
          onChange={(newValue, context) => {
            if (newValue === null) {
              setCommittedValue(null)
              onChange(null)
              if (enableTypedDdMmYyyy) setIsEditing(false)
              return
            }
            if (enableTypedDdMmYyyy && isEditing) {
              setDraftInput(context?.inputValue ?? '')
              return
            }
            if (newValue instanceof Date && !Number.isNaN(newValue.getTime())) {
              setCommittedValue(newValue)
              onChange(newValue)
            }
          }}
          onClose={() => {
            setIsOpen(false)
            if (enableTypedDdMmYyyy) setIsEditing(false)
          }}
          className={errorMessage ? 'Mui-error' : ''}
          open={isOpen}
          value={committedValue ?? null}
          format={effectiveFormat}
          timezone="UTC"
          minDate={minValue}
          maxDate={maxValue}
          ref={datePickerRef}
          slots={datePickerSlots}
          slotProps={{
            actionBar: { actions: clearable ? ['today', 'clear'] : ['today'] },
            field: {
              clearable: true,
              disabled,
              readOnly,
              placeholder: '',
              onMouseDownCapture: () => {
                if (enableTypedDdMmYyyy && !isEditing) setIsEditing(true)
              },
              onClick: (e) => e.stopPropagation(),
              onFocus: () => {
                if (enableTypedDdMmYyyy && !isEditing) {
                  defer(() => setIsEditing(true))
                }
              },
              onKeyDown: (e) => {
                if (!enableTypedDdMmYyyy) return
                if (e.key === 'Enter') {
                  const raw = e.currentTarget.value
                  setDraftInput(raw)
                  const parsed = parseTypedDate(raw)
                  e.preventDefault()
                  e.stopPropagation()
                  if (parsed) {
                    setCommittedValue(parsed)
                    onChange(parsed)
                    setIsEditing(false)
                  }
                }
                const isTypingKey = e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete'
                if (!isEditing && isTypingKey) {
                  setIsEditing(true)
                }
              },
              onBlur: (e) => {
                if (!enableTypedDdMmYyyy || !isEditing) return
                const raw = e.currentTarget?.value ?? draftInput
                setDraftInput(raw)
                const parsed = parseTypedDate(raw)
                if (parsed) {
                  setCommittedValue(parsed)
                  onChange(parsed)
                  setIsEditing(false)
                }
              },
              inputRef: fieldInputRef
            },
            popper: {
              anchorEl: () => (showAdornment ? fieldInputRef.current : anchorEl.current),
              placement: 'bottom-start'
            },
            openPickerButton: {
              onClick: (e) => {
                e.stopPropagation()
                setIsOpen(true)
                if (enableTypedDdMmYyyy) setIsEditing(false)
              }
            },
            inputAdornment: {
              position: showAdornment ? 'start' : 'end',
              onClick: (e) => {
                if (!showAdornment) return
                e.stopPropagation()
                setIsOpen(true)
                if (enableTypedDdMmYyyy) setIsEditing(false)
              },
              toggleDatePicker,
              largeAdornment,
              sx: showAdornment ? { cursor: 'pointer' } : undefined
            }
          }}
          sx={{
            width: '100%',
            height,
            input: { height: '10px' },
            '.MuiInputBase-root': {
              backgroundColor: 'white',
              borderColor: errorMessage ? 'error.main' : 'text.secondary',
              height: sx?.height || height,
              mb: 0,
              pl: 0
            },
            legend: {
              width: label ? 'auto' : '0px'
            },
            '&.Mui-error': {
              '.MuiInputLabel-root': {
                color: 'error.main'
              },
              fieldset: {
                borderColor: 'error.main'
              }
            },
            '.MuiInputAdornment-root': {
              display: showAdornment ? 'flex' : 'none'
            },
            ...(!disabled &&
              !readOnly && {
                '.MuiInputBase-root.Mui-disabled, .MuiInputBase-root.Mui-readOnly, .MuiInputBase-input.Mui-disabled, .MuiInputBase-input.Mui-readOnly':
                  {
                    backgroundColor: 'white !important',
                    color: 'inherit !important',
                    cursor: 'text !important',
                    pointerEvents: 'auto !important',
                    userSelect: 'auto !important',
                    WebkitTextFillColor: 'inherit !important'
                  },
                '.MuiInputBase-root.Mui-disabled fieldset, .MuiInputBase-root.Mui-readOnly fieldset': {
                  borderColor: 'rgba(5, 28, 51, 0.23) !important'
                }
              })
          }}
        />
      </LocalizationProvider>
    </MUIStack>
  )
}

DateSelection.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  label: PropTypes.string,
  sx: PropTypes.object,
  required: PropTypes.bool,
  format: PropTypes.string,
  size: PropTypes.string,
  timeRecordingDateFilter: PropTypes.bool,
  dataGridFilter: PropTypes.bool,
  setBeforeAfterOpen: PropTypes.func,
  comparisonOperator: PropTypes.number,
  showAdornment: PropTypes.bool,
  name: PropTypes.string,
  errors: PropTypes.object,
  minValue: PropTypes.object,
  maxValue: PropTypes.object,
  clearable: PropTypes.bool,
  enableTypedDdMmYyyy: PropTypes.bool,
  largeAdornment: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  isInlineEditMode: PropTypes.bool
}

const Adornment = ({ toggleDatePicker, onClick, largeAdornment, disabled = false }) => (
  <MUIInputAdornment position="start" sx={{ pointerEvents: disabled ? 'none' : 'auto', minHeight: '100%', height: 'auto' }}>
    <MUIStack
      direction="row"
      alignItems="center"
      justifyContent="center"
      onClick={(e) => {
        if (toggleDatePicker) {
          toggleDatePicker(e)
        } else if (onClick) {
          onClick(e)
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (toggleDatePicker) toggleDatePicker(e)
          else if (onClick) onClick(e)
        }
      }}
      sx={{
        width: '40px',
        height: largeAdornment ? '53px' : '40px',
        backgroundColor: 'rgba(61, 81, 123, 0.04)',
        userSelect: 'none',
        borderLeft: 'none',
        borderRight: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px 0px 0px 4px',
        cursor: 'pointer',
        zIndex: 1,
        minHeight: '100%'
      }}
    >
      <DateIcon sx={{ width: '22px', height: '22px' }} />
    </MUIStack>
  </MUIInputAdornment>
)

Adornment.propTypes = {
  toggleDatePicker: PropTypes.func,
  onClick: PropTypes.func,
  position: PropTypes.oneOf(['start', 'end']),
  sx: PropTypes.object,
  largeAdornment: PropTypes.bool,
  disabled: PropTypes.bool
}
